import React, { useEffect, useRef } from 'react'
import { Page } from '../../Elements/PageMargins'
import { NotificationFormCard } from './NotificationFormCard'
import { useParams } from 'react-router'
import { useNotificationContext } from '../../Context/NotificationContext'
import { useMountEffect } from '../../../hooks/useMountEffect'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { Notification, notificationApi } from '../../../api/swagger'
import { useTranslation } from 'react-i18next'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../../Context/SnackbarContext'
import { useErrorSnackbar } from '../../../hooks/useErrorSnackbar'

export const NotificationDetails: React.FC = () => {
  const { t } = useTranslation()
  const {
    selectedNotificationKey,
    updateSelectedNotificationKey,
    notificationDetails,
    updateNotificationDetails,
  } = useNotificationContext()
  const { notificationKey } = useParams<{ notificationKey: string }>()
  const {
    Notification: { fetchNotification },
  } = useLoadingIds()
  const { setSnackbarMessage, setSnackbarSeverity, setSnackbarState } =
    useSnackbarContext()
  const { handleError } = useErrorSnackbar()
  /** If we refresh, pull and use the notificationKey from the url */
  useMountEffect(() => {
    if (!selectedNotificationKey && !!notificationKey) {
      updateSelectedNotificationKey(+notificationKey)
    }
  })

  const initialNotificationDetails = useRef<Notification>()

  const fetchNotificationDetails = async () => {
    try {
      if (selectedNotificationKey === undefined) return
      const response = await notificationApi.fetchNotification({
        notificationKey: selectedNotificationKey,
      })
      initialNotificationDetails.current = response.notification
      updateNotificationDetails(response.notification)
    } catch (error) {
      handleError(
        error,
        t(
          'Admin.NotificationContext.FetchNotifications.FailedToFetch',
          'Unable to retrieve notification details'
        )
      )
    }
  }

  const { triggerFetch } = useLoadingContext({
    loadingId: fetchNotification,
    asyncFunction: fetchNotificationDetails,
  })

  useEffect(() => {
    if (
      !notificationDetails ||
      (!!notificationDetails &&
        !!selectedNotificationKey &&
        notificationDetails.notificationKey !== selectedNotificationKey)
    ) {
      triggerFetch()
    }
  }, [
    notificationDetails,
    fetchNotification,
    triggerFetch,
    selectedNotificationKey,
  ])

  const handleFormSubmit = async () => {
    try {
      /**
       * AFAIK, we don't care about the return value of the edit
       * because we should have all the updated information on success.
       *
       * On failure, we'd still have to Cancel to revert details.
       */
      await notificationApi.editNotification({
        body: {
          notification: notificationDetails,
        },
      })
      setSnackbarMessage(
        t(
          'Notifications.AddNotification.EditNotification.Success',
          'Notification successfully updated.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Success)
      setSnackbarState(true)
    } catch (e) {
      handleError(
        e,
        t(
          'Notifications.AddNotification.EditNotification.DefaultError',
          'An unknown error occurred updating the notification details.'
        )
      )
    }
  }

  const handleCancel = () => {
    updateNotificationDetails(
      initialNotificationDetails.current ?? notificationDetails
    )
  }

  return (
    <Page>
      <NotificationFormCard
        isEditVariant
        handleFormSubmit={handleFormSubmit}
        handleCancel={handleCancel}
      />
    </Page>
  )
}
